'use client';

import { forwardRef, useCallback, useRef, type PointerEvent } from 'react';
import { styled, useThemeProps } from '@mui/material/styles';

interface ColumnResizerProps {
    /** Called continuously during drag with the horizontal pixel delta since the last event. */
    onResize?: (delta: number) => void;
    className?: string;
}

const ColumnResizerRoot = styled('div', {
    name: 'MuiColumnResizer',
    slot: 'root',
})(({ theme }) => ({
    width: 4,
    height: '100%',
    position: 'absolute',
    right: 0,
    top: 0,
    cursor: 'col-resize',
    backgroundColor: 'transparent',
    touchAction: 'none',
    zIndex: 1,
    '&:hover, &:active': {
        backgroundColor: theme.palette.primary.main,
    },
}));

const ColumnResizer = forwardRef<HTMLDivElement, ColumnResizerProps>(
    function ColumnResizer(inProps, ref) {
        const props = useThemeProps({ props: inProps, name: 'MuiColumnResizer' });
        const { onResize, className, ...other } = props;

        const lastXRef = useRef(0);

        const handlePointerDown = useCallback((e: PointerEvent<HTMLDivElement>) => {
            e.preventDefault();
            const target = e.currentTarget;
            target.setPointerCapture(e.pointerId);
            lastXRef.current = e.clientX;
        }, []);

        const handlePointerMove = useCallback(
            (e: PointerEvent<HTMLDivElement>) => {
                if (!e.currentTarget.hasPointerCapture(e.pointerId)) {
                    return;
                }
                const delta = e.clientX - lastXRef.current;
                lastXRef.current = e.clientX;
                onResize?.(delta);
            },
            [onResize]
        );

        const handlePointerUp = useCallback((e: PointerEvent<HTMLDivElement>) => {
            e.currentTarget.releasePointerCapture(e.pointerId);
        }, []);

        return (
            <ColumnResizerRoot
                ref={ref}
                className={className}
                onPointerDown={handlePointerDown}
                onPointerMove={handlePointerMove}
                onPointerUp={handlePointerUp}
                {...other}
            />
        );
    }
);

export { ColumnResizer };
export type { ColumnResizerProps };
